import { useState, useEffect } from "react";
import { Loader2, Briefcase, CheckCircle, AlertCircle } from "lucide-react";
import useAxiosAdmin from "../../../../lib/axiosAdmin";

const StaffServicesTab = ({ formData, setFormData, editMode, staffId }) => {
  const axiosAdmin = useAxiosAdmin();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedServices, setExpandedServices] = useState(new Set());

  useEffect(() => {
    fetchServices();
  }, []);

  // بارگذاری و تنظیم expanded services در حالت ویرایش
  useEffect(() => {
    if (editMode && formData.services && formData.services.length > 0 && services.length > 0) {
      // باز کردن خودکار سرویس‌های فعال
      const activeServiceIds = formData.services.map((s) => {
        // اگر serviceId یک object است، _id آن را برمی‌گردانیم
        return typeof s.serviceId === 'object' ? s.serviceId._id : s.serviceId;
      });
      setExpandedServices(new Set(activeServiceIds));
    }
  }, [editMode, formData.services, services]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const res = await axiosAdmin.get("/api/admin/services?all=true");
      setServices(res.data.services || []);
    } catch (error) {
      console.error("خطا در دریافت خدمات:", error);
    } finally {
      setLoading(false);
    }
  };

  // استخراج ID واقعی از serviceId (چون ممکنه object باشه)
  const getServiceId = (serviceId) => {
    return typeof serviceId === 'object' ? serviceId._id : serviceId;
  };

  const isServiceEnabled = (serviceId) => {
    return formData.services?.some((s) => getServiceId(s.serviceId) === serviceId) || false;
  };

  const getServiceData = (serviceId) => {
    return formData.services?.find((s) => getServiceId(s.serviceId) === serviceId) || null;
  };

  const toggleService = (service) => {
    const isEnabled = isServiceEnabled(service._id);

    if (isEnabled) {
      // حذف خدمت
      setFormData((prev) => ({
        ...prev,
        services: prev.services.filter((s) => getServiceId(s.serviceId) !== service._id),
      }));

      // حذف از لیست باز شده‌ها
      setExpandedServices((prev) => {
        const newSet = new Set(prev);
        newSet.delete(service._id);
        return newSet;
      });
    } else {
      // اضافه کردن خدمت با مقادیر پیشفرض
      setFormData((prev) => ({
        ...prev,
        services: [
          ...(prev.services || []),
          {
            serviceId: service._id,
            kasht: false,
            retoosh: false,
            tarmim: false,
            lift: false,
            normal: {
              enabled: false,
            },
            vip: {
              enabled: false,
            },
          },
        ],
      }));

      // باز کردن خودکار
      setExpandedServices((prev) => new Set(prev).add(service._id));
    }
  };

  const toggleExpand = (serviceId) => {
    setExpandedServices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };

  const updateServiceField = (serviceId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.map((s) =>
        getServiceId(s.serviceId) === serviceId ? { ...s, [field]: value } : s
      ),
    }));
  };

  const updateServiceNested = (serviceId, parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.map((s) =>
        getServiceId(s.serviceId) === serviceId
          ? {
              ...s,
              [parent]: {
                ...s[parent],
                [field]: value,
              },
            }
          : s
      ),
    }));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-purple-500 animate-spin mb-3" />
        <p className="text-slate-500 text-sm">در حال بارگذاری خدمات...</p>
      </div>
    );
  }

  if (!services.length) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-10 text-center">
        <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 text-sm">هیچ خدمتی در سیستم ثبت نشده است</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* راهنما */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Briefcase className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">راهنمای تنظیم خدمات:</p>
          <ul className="list-disc list-inside space-y-1 text-blue-700">
            <li>ابتدا خدمت مورد نظر را فعال کنید</li>
            <li>سپس زیرمجموعه‌های مورد نظر (کاشت، روتوش، ترمیم، لیفت) را انتخاب کنید</li>
            <li>نوع ارائه خدمت (عادی یا VIP) را مشخص کنید</li>
            <li>می‌توانید چند خدمت را همزمان فعال کنید</li>
          </ul>
        </div>
      </div>

      {/* لیست خدمات */}
      <div className="space-y-4">
        {services.map((service) => {
          const isEnabled = isServiceEnabled(service._id);
          const isExpanded = expandedServices.has(service._id);
          const serviceData = getServiceData(service._id);

          return (
            <div
              key={service._id}
              className={`
                border-2 rounded-xl overflow-hidden transition-all duration-300
                ${
                  isEnabled
                    ? "border-purple-300 bg-purple-50/50 shadow-md shadow-purple-500/10"
                    : "border-slate-200 bg-white"
                }
              `}
            >
              {/* هدر خدمت */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div
                    className={`
                    w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-colors
                    ${
                      isEnabled
                        ? "bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg shadow-purple-500/30"
                        : "bg-slate-100"
                    }
                  `}
                  >
                    {isEnabled ? "✅" : "💼"}
                  </div>

                  <div className="flex-1">
                    <h3 className="text-base font-bold text-slate-800">
                      {service.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      {service.normal?.enabled && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                          عادی
                        </span>
                      )}
                      {service.vip?.enabled && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded">
                          VIP
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* دکمه فعال/غیرفعال */}
                <div className="flex items-center gap-3">
                  {isEnabled && (
                    <button
                      onClick={() => toggleExpand(service._id)}
                      className="text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors"
                    >
                      {isExpanded ? "بستن ▲" : "تنظیمات ▼"}
                    </button>
                  )}

                  <button
                    onClick={() => toggleService(service)}
                    className={`
                      relative w-14 h-7 rounded-full transition-all duration-300
                      ${
                        isEnabled
                          ? "bg-gradient-to-r from-purple-500 to-purple-600"
                          : "bg-slate-300"
                      }
                    `}
                  >
                    <div
                      className={`
                        absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300
                        ${isEnabled ? "right-1" : "right-8"}
                      `}
                    />
                  </button>
                </div>
              </div>

              {/* محتوای باز شده */}
              {isEnabled && isExpanded && serviceData && (
                <div className="border-t border-purple-200 bg-white p-5 space-y-5">
                  {/* زیرمجموعه‌ها */}
                  <div>
                    <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-purple-600" />
                      انتخاب زیرمجموعه‌ها
                    </h4>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {/* کاشت */}
                      {service.kasht && (
                        <label className="flex items-center gap-2 p-3 border-2 border-slate-200 rounded-lg cursor-pointer hover:border-purple-300 hover:bg-purple-50/50 transition-all group">
                          <input
                            type="checkbox"
                            checked={serviceData.kasht || false}
                            onChange={(e) =>
                              updateServiceField(
                                service._id,
                                "kasht",
                                e.target.checked
                              )
                            }
                            className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-slate-700 group-hover:text-purple-700">
                            کاشت
                          </span>
                        </label>
                      )}

                      {/* روتوش */}
                      {service.retoosh && (
                        <label className="flex items-center gap-2 p-3 border-2 border-slate-200 rounded-lg cursor-pointer hover:border-purple-300 hover:bg-purple-50/50 transition-all group">
                          <input
                            type="checkbox"
                            checked={serviceData.retoosh || false}
                            onChange={(e) =>
                              updateServiceField(
                                service._id,
                                "retoosh",
                                e.target.checked
                              )
                            }
                            className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-slate-700 group-hover:text-purple-700">
                            روتوش
                          </span>
                        </label>
                      )}

                      {/* ترمیم */}
                      {service.tarmim && (
                        <label className="flex items-center gap-2 p-3 border-2 border-slate-200 rounded-lg cursor-pointer hover:border-purple-300 hover:bg-purple-50/50 transition-all group">
                          <input
                            type="checkbox"
                            checked={serviceData.tarmim || false}
                            onChange={(e) =>
                              updateServiceField(
                                service._id,
                                "tarmim",
                                e.target.checked
                              )
                            }
                            className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-slate-700 group-hover:text-purple-700">
                            ترمیم
                          </span>
                        </label>
                      )}

                      {/* لیفت */}
                      {service.lift && (
                        <label className="flex items-center gap-2 p-3 border-2 border-slate-200 rounded-lg cursor-pointer hover:border-purple-300 hover:bg-purple-50/50 transition-all group">
                          <input
                            type="checkbox"
                            checked={serviceData.lift || false}
                            onChange={(e) =>
                              updateServiceField(
                                service._id,
                                "lift",
                                e.target.checked
                              )
                            }
                            className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-slate-700 group-hover:text-purple-700">
                            لیفت
                          </span>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* نوع ارائه */}
                  <div>
                    <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-purple-600" />
                      نوع ارائه خدمت
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* عادی */}
                      {service.normal?.enabled && (
                        <div className="flex items-center justify-between p-4 border-2 border-slate-200 rounded-lg bg-green-50/50">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center text-white font-bold">
                              N
                            </div>
                            <div>
                              <p className="text-sm font-bold text-slate-800">
                                عادی
                              </p>
                              <p className="text-xs text-slate-500">
                                {service.normal.price?.toLocaleString()} تومان
                              </p>
                            </div>
                          </div>

                          <button
                            onClick={() =>
                              updateServiceNested(
                                service._id,
                                "normal",
                                "enabled",
                                !serviceData.normal?.enabled
                              )
                            }
                            className={`
                              relative w-12 h-6 rounded-full transition-all duration-300
                              ${
                                serviceData.normal?.enabled
                                  ? "bg-green-500"
                                  : "bg-slate-300"
                              }
                            `}
                          >
                            <div
                              className={`
                                absolute top-1 w-4 h-4 bg-white rounded-full shadow-md transition-all duration-300
                                ${
                                  serviceData.normal?.enabled
                                    ? "right-1"
                                    : "right-7"
                                }
                              `}
                            />
                          </button>
                        </div>
                      )}

                      {/* VIP */}
                      {service.vip?.enabled && (
                        <div className="flex items-center justify-between p-4 border-2 border-slate-200 rounded-lg bg-amber-50/50">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center text-white font-bold">
                              V
                            </div>
                            <div>
                              <p className="text-sm font-bold text-slate-800">
                                VIP
                              </p>
                              <p className="text-xs text-slate-500">
                                {service.vip.price?.toLocaleString()} تومان
                              </p>
                            </div>
                          </div>

                          <button
                            onClick={() =>
                              updateServiceNested(
                                service._id,
                                "vip",
                                "enabled",
                                !serviceData.vip?.enabled
                              )
                            }
                            className={`
                              relative w-12 h-6 rounded-full transition-all duration-300
                              ${
                                serviceData.vip?.enabled
                                  ? "bg-gradient-to-r from-amber-400 to-amber-500"
                                  : "bg-slate-300"
                              }
                            `}
                          >
                            <div
                              className={`
                                absolute top-1 w-4 h-4 bg-white rounded-full shadow-md transition-all duration-300
                                ${
                                  serviceData.vip?.enabled ? "right-1" : "right-7"
                                }
                              `}
                            />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* خلاصه انتخاب‌ها */}
      {formData.services && formData.services.length > 0 && (
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-xl p-5">
          <h3 className="text-base font-bold text-purple-900 mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            خلاصه خدمات انتخاب شده ({formData.services.length})
          </h3>
          <div className="space-y-2">
            {formData.services.map((s) => {
              const serviceId = getServiceId(s.serviceId);
              const service = services.find((srv) => srv._id === serviceId);
              
              // اگر serviceId یک object است، از name آن استفاده می‌کنیم
              const serviceName = service?.name || (typeof s.serviceId === 'object' ? s.serviceId.name : 'نامشخص');
              
              const selectedSubs = [];
              if (s.kasht) selectedSubs.push("کاشت");
              if (s.retoosh) selectedSubs.push("روتوش");
              if (s.tarmim) selectedSubs.push("ترمیم");
              if (s.lift) selectedSubs.push("لیفت");

              const types = [];
              if (s.normal?.enabled) types.push("عادی");
              if (s.vip?.enabled) types.push("VIP");

              return (
                <div
                  key={serviceId}
                  className="bg-white rounded-lg p-3 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-bold text-slate-800">
                      {serviceName}
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      {selectedSubs.length > 0
                        ? selectedSubs.join(" • ")
                        : "هیچ زیرمجموعه‌ای انتخاب نشده"}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {types.map((type) => (
                      <span
                        key={type}
                        className={`
                          px-2 py-1 text-xs font-medium rounded
                          ${
                            type === "VIP"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-green-100 text-green-700"
                          }
                        `}
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffServicesTab;